<template>
  <div
    ref="tooltip"
    class="card-tooltip"
    :class="{
      'card-tooltip_is-show': isShow,
      'card-tooltip_no-icon': !showIcon,
    }"
    @mouseenter="show"
    @mouseleave="hide"
    @focusout="hide"
  >
    <slot />

    <atomic-icon
      v-if="showIcon"
      :id="icon || 'info'"
    />

    <Teleport to="body">
      <div
        ref="tooltipMsg"
        class="card-tooltip__message"
        :class="[
          messageCustomClass,
          { 'card-tooltip__message_active': isShow },
          `card-tooltip__message_size_${size || 'large'}`,
        ]"
        :style="`top: ${coords.top}px; left: ${coords.left}px`"
      >
        <template v-if="text || title">
          <div
            v-if="title"
            class="card-tooltip__title"
          >
            {{ title }}
          </div>
          <div
            v-if="text"
            class="card-tooltip__text"
            v-html="text"
          />
        </template>

        <template v-else>
          <slot name="custom" />
        </template>

        <div
          class="card-tooltip__triangle"
          :style="{ left: `${arrowLeft}px` }"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  icon?: string;
  title?: string;
  text?: string;
  align?: 'bottom';
  messageCustomClass?: string;
  size?: 'small' | 'large';
  delayedHide?: boolean;
  showIcon?: boolean;
}>(), {
  showIcon: true,
});

const coords = reactive({ top: 0, left: 0 });
const arrowLeft = ref(0);
const HIDE_DELAY = 2000;

const tooltip = ref<HTMLElement>();
const tooltipMsg = ref<HTMLElement>();
const isShow = ref(false);

const getTooltipParent = () => tooltip.value?.closest('[data-tooltip-parent]');

const setCoords = () => {
  const parent = getTooltipParent();
  const parentRect = parent?.getBoundingClientRect();
  const tooltipRect = tooltip.value?.getBoundingClientRect();
  const msgRect = tooltipMsg.value?.getBoundingClientRect();

  if (tooltipRect && msgRect) {
    const tooltipCenter = tooltipRect.left + tooltipRect.width / 2;
    let leftPosition = tooltipCenter - msgRect.width / 2;

    if (parent && parentRect) {
      const minLeft = parentRect.left;
      const maxLeft = parentRect.right - msgRect.width;

      if (leftPosition < minLeft) {
        leftPosition = minLeft;
      }
      else if (leftPosition > maxLeft) {
        leftPosition = maxLeft;
      }
    }

    coords.left = leftPosition;
    coords.top = tooltipRect.top - msgRect.height - 8;

    arrowLeft.value = tooltipCenter - leftPosition - 7;
  }
};

const show = () => {
  setCoords();
  isShow.value = true;
};

const hide = () => {
  if (props.delayedHide) {
    setTimeout(() => {
      isShow.value = false;
    }, HIDE_DELAY);
  }
  else {
    isShow.value = false;
  }
};

onMounted(async () => {
  await nextTick();

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('touchmove', hide, { passive: true });

  const scrollParent = getTooltipParent();
  if (scrollParent) {
    scrollParent.addEventListener('scroll', hide, { passive: true });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', hide);
  window.removeEventListener('touchmove', hide);

  const scrollParent = getTooltipParent();
  if (scrollParent) {
    scrollParent.removeEventListener('scroll', hide);
  }
});
</script>

<style src="~/assets/styles/components/atomic/card-tooltip.scss" lang="scss" />
